import { useCallback, useState, useEffect, useMemo } from 'react';
import log from 'loglevel';
import { useTenant } from '../contexts/TenantContext';
import { TenantType, Tenant } from '../types/tenant.types';

// Storage key for impersonation state
const IMPERSONATION_ORIGIN_KEY = 'impersonation-origin-tenant';

interface ImpersonationState {
  /**
   * Whether impersonation mode is currently active
   */
  isImpersonating: boolean;

  /**
   * The origin tenant (Master) that initiated the impersonation
   */
  originTenant: Tenant | null;

  /**
   * The target tenant being impersonated (client)
   */
  targetTenant: Tenant | null;

  /**
   * Start impersonating a client tenant (switches from Master to client)
   */
  startImpersonation: (targetTenantId: string) => Promise<void>;

  /**
   * End impersonation and return to the Master tenant
   */
  endImpersonation: () => Promise<void>;

  /**
   * Whether the current tenant can initiate impersonation (is Master type)
   */
  canImpersonate: boolean;
}

/**
 * Hook for managing impersonation state
 *
 * Impersonation allows a Master tenant user to temporarily switch to a client
 * tenant while maintaining the ability to return to the Master tenant.
 *
 * Usage:
 * ```tsx
 * const { isImpersonating, startImpersonation, endImpersonation, canImpersonate } = useImpersonation();
 *
 * // In Master tenant, start impersonation
 * if (canImpersonate) {
 *   <Button onClick={() => startImpersonation(clientTenantId)}>Login as Client</Button>
 * }
 *
 * // Show banner when impersonating
 * if (isImpersonating) {
 *   <ImpersonationBanner onExit={endImpersonation} />
 * }
 * ```
 */
export function useImpersonation(): ImpersonationState {
  const { selectedTenant, availableTenants, switchTenant } = useTenant();
  const [originTenant, setOriginTenant] = useState<Tenant | null>(null);

  // Check if the selected tenant is of type MASTER
  const isMasterTenant = selectedTenant?.type === TenantType.MASTER;

  // Load impersonation state from storage on mount
  useEffect(() => {
    try {
      const storedOrigin = sessionStorage.getItem(IMPERSONATION_ORIGIN_KEY);
      if (storedOrigin) {
        const parsed = JSON.parse(storedOrigin) as Tenant;
        setOriginTenant(parsed);
        log.info('🔄 Restored impersonation state, origin tenant:', parsed.name);
      }
    } catch (error) {
      log.error('Failed to restore impersonation state:', error);
      sessionStorage.removeItem(IMPERSONATION_ORIGIN_KEY);
    }
  }, []);

  // Whether impersonation is currently active
  const isImpersonating = useMemo(() => {
    return originTenant !== null;
  }, [originTenant]);

  // Whether the current tenant can initiate impersonation
  const canImpersonate = useMemo(() => {
    return isMasterTenant && !isImpersonating;
  }, [isMasterTenant, isImpersonating]);

  // The target tenant being impersonated
  const targetTenant = useMemo(() => {
    if (!isImpersonating) {
      return null;
    }
    return selectedTenant;
  }, [isImpersonating, selectedTenant]);

  /**
   * Start impersonating a client tenant
   */
  const startImpersonation = useCallback(
    async (targetTenantId: string) => {
      if (!selectedTenant) {
        throw new Error('No tenant selected');
      }

      if (selectedTenant.type !== TenantType.MASTER) {
        throw new Error('Only Master tenant can initiate impersonation');
      }

      // Store the current (Master) tenant as origin
      sessionStorage.setItem(IMPERSONATION_ORIGIN_KEY, JSON.stringify(selectedTenant));
      setOriginTenant(selectedTenant);

      log.info(`🎭 Starting impersonation: Master -> ${targetTenantId}`);

      // Switch to the target tenant
      await switchTenant(targetTenantId);
    },
    [selectedTenant, switchTenant],
  );

  /**
   * End impersonation and return to the Master tenant
   */
  const endImpersonation = useCallback(async () => {
    if (!originTenant) {
      throw new Error('Not currently impersonating');
    }

    const originTenantId = originTenant.id;

    log.info(`🎭 Ending impersonation: returning to ${originTenant.name}`);

    // Clear impersonation state
    sessionStorage.removeItem(IMPERSONATION_ORIGIN_KEY);
    setOriginTenant(null);

    // Find the origin tenant in available tenants
    const originUserTenant = availableTenants.find((ut) => ut.tenantId === originTenantId);
    if (!originUserTenant) {
      log.error('Origin tenant not found in available tenants');
      throw new Error('Origin tenant not accessible');
    }

    // Switch back to the Master tenant
    await switchTenant(originTenantId);
  }, [originTenant, availableTenants, switchTenant]);

  return {
    isImpersonating,
    originTenant,
    targetTenant,
    startImpersonation,
    endImpersonation,
    canImpersonate,
  };
}

export default useImpersonation;

